import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CustomerBanModal from "../../../../../components/dashboardcomponents/People/Customers/CustomerBanModal";
import CustomerDeleteModal from "../../../../../components/dashboardcomponents/People/Customers/CustomerDeleteModal";
import CustomerDetailHeaderBand from "../../../../../components/dashboardcomponents/People/Customers/CustomerDetail/CustomerDetailHeaderBand";
import CustomerDetailInfoSection from "../../../../../components/dashboardcomponents/People/Customers/CustomerDetail/CustomerDetailInfoSection";
import CustomerDetailGroupsTeam from "../../../../../components/dashboardcomponents/People/Customers/CustomerDetail/CustomerDetailGroupsTeam";
import CustomerDetailTags from "../../../../../components/dashboardcomponents/People/Customers/CustomerDetail/CustomerDetailTags";
import CustomerDetailHistory from "../../../../../components/dashboardcomponents/People/Customers/CustomerDetail/CustomerDetailHistory";
import { buildCustomerDetailView } from "../../../../../components/dashboardcomponents/People/Customers/CustomerDetail/customerDetailHelpers";
import { buildCustomerBanPayload } from "../../../../../utils/customerMappers";
import {
  CUSTOMER_DETAIL_BAN,
  CUSTOMER_DETAIL_DELETE,
} from "../../../../../components/dashboardcomponents/People/Customers/customerEvents";
import { CustomerDetailPageSkeleton } from "../../../../../components/dashboardcomponents/People/Customers/CustomerLoadingSkeletons";
import {
  banCustomer as banCustomerApi,
  clearCustomerDetail,
  deleteCustomer as deleteCustomerApi,
  fetchCustomerById,
} from "../../../../../store/features/customersApi";

export default function CustomerDetailPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [banOpen, setBanOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { detail, detailLoading, mutationLoading } = useSelector((state) => state.peopleCustomers);

  useEffect(() => {
    if (customerId) dispatch(fetchCustomerById({ customerId }));
    return () => dispatch(clearCustomerDetail());
  }, [customerId, dispatch]);

  const customer = useMemo(
    () => (detail?.id === customerId ? buildCustomerDetailView(detail) : null),
    [detail, customerId]
  );

  useEffect(() => {
    const onBan = () => setBanOpen(true);
    const onDelete = () => setDeleteOpen(true);
    window.addEventListener(CUSTOMER_DETAIL_BAN, onBan);
    window.addEventListener(CUSTOMER_DETAIL_DELETE, onDelete);
    return () => {
      window.removeEventListener(CUSTOMER_DETAIL_BAN, onBan);
      window.removeEventListener(CUSTOMER_DETAIL_DELETE, onDelete);
    };
  }, []);

  if (detailLoading && !customer) {
    return <CustomerDetailPageSkeleton />;
  }

  if (!customer) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ fontFamily: "Inter", color: "#405261" }}>
          Customer not found.
        </Typography>
      </Box>
    );
  }

  const handleBanConfirm = async (duration) => {
    const body = buildCustomerBanPayload(duration);
    const result = await dispatch(banCustomerApi({ customerId, body }));
    if (banCustomerApi.fulfilled.match(result)) {
      toast.success(result.payload?.message || `${customer.name} has been banned`);
      setBanOpen(false);
      navigate("/people/customers");
    } else {
      toast.error(result.payload?.message || "Failed to ban customer");
    }
  };

  const handleDeleteConfirm = async () => {
    if (mutationLoading) return;
    const result = await dispatch(deleteCustomerApi({ customerId }));
    if (deleteCustomerApi.fulfilled.match(result)) {
      toast.success(result.payload?.message || "Customer deleted");
      setDeleteOpen(false);
      navigate("/people/customers");
    } else {
      toast.error(result.payload?.message || "Failed to delete customer");
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "#FFF",
      }}
    >
      <CustomerDetailHeaderBand customer={customer} />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          pt: "32px",
          px: 3,
          pb: 3,
        }}
      >
        <Box sx={{ maxWidth: 632, width: "100%", mx: "auto" }}>
          <CustomerDetailInfoSection customer={customer} />
          <CustomerDetailGroupsTeam customer={customer} />
          <CustomerDetailTags tags={customer.tags} />
          <CustomerDetailHistory history={customer.history} />
        </Box>
      </Box>

      <CustomerBanModal
        open={banOpen}
        customer={customer}
        onClose={() => setBanOpen(false)}
        onConfirm={handleBanConfirm}
      />

      <CustomerDeleteModal
        open={deleteOpen}
        customerName={customer.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        loading={mutationLoading}
      />
    </Box>
  );
}
